import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";
import { ChunkQueue } from "./helpers/chunkQueue";
import { ChunkProcessor } from "./helpers/chunkProcessor";
import {
  webSocketManager,
  usesBackendProxy,
  RecordingMetadata,
} from "./helpers/webSocket";
import { getIcsStreamTicket } from "../../api/icsStreamApi";
import { useMediaRecorder } from "./hooks/useMediaRecorder";
import { useTimer } from "./hooks/useTimer";
import { Maximize2, Minimize2 } from "react-feather";
import colors from "../../shared/constants/color";
import { textBase, textSM } from "../../shared/constants/textStyles";
import Recording from "../../shared/images/icons/recording.svg";
import { useToast } from "../../shared/hooks";
import { setupLogger } from "./helpers/logger";
import { Prompt, useHistory } from "react-router-dom";

interface ScreenRecorderProps {
  interviewId: string;
  webSocketUrl: string;
  onClose?: () => void;
}

const ScreenRecorder: React.FC<ScreenRecorderProps> = ({
  interviewId,
  webSocketUrl,
  onClose,
}) => {
  const [selectedSource, setSelectedSource] = useState<string>("");
  const [availableSources, setAvailableSources] = useState<
    { id: string; name: string }[]
  >([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordingMetadata, setRecordingMetadata] =
    useState<RecordingMetadata | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [isCompact, setIsCompact] = useState(false);
  const [displayStream, setDisplayStream] = useState<MediaStream | null>(null);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const isRecordingRef = useRef(false);
  const chunkQueueRef = useRef(new ChunkQueue());
  const chunkProcessorRef = useRef(new ChunkProcessor(chunkQueueRef.current));

  const timer = useTimer();
  const uploadTimer = useTimer();
  const { showToast } = useToast();
  const history = useHistory();

  const {
    startRecording,
    stopRecording,
    isRecording,
    error: recorderError,
  } = useMediaRecorder({
    chunkQueue: chunkQueueRef.current,
    onStopRecording: handleStopRecordingCallback,
    displayStream,
  });

  // Setup logger on mount
  // useEffect(() => {
  //   const environment = process.env.NODE_ENV || "development";
  //   setupLogger(environment, interviewId);
  //   console.log("Logger initialized for screen recorder");
  // }, [interviewId]);

  useEffect(() => {
    // Connect WebSocket on mount
    const fileName = uuidv4();

    let cancelled = false;

    const connect = (ticket?: string) =>
      webSocketManager.connectWebSocket({
        serverUrl: webSocketUrl,
        interviewId,
        fileName,
        ticket,
        onOpen: () => {
          console.log("WebSocket connected");
          setIsConnected(true);
          chunkProcessorRef.current.setSocketStatus(true);
          chunkProcessorRef.current.startChunkProcessing();
        },
        onMessage: (data: RecordingMetadata) => {
          setRecordingMetadata(data);
        },
        onError: (err: Event) => {
          console.error("WebSocket error:", err);
          setError("Sunucuya bağlanılamadı. Kayıt başlatılamıyor.");
          chunkProcessorRef.current.setSocketStatus(false);
        },
        onClose: (event: CloseEvent) => {
          setIsConnected(false);
          chunkProcessorRef.current.setSocketStatus(false);

          const who =
            event.code === 1000
              ? "frontend"
              : event.code === 1006
                ? "backend"
                : "unknown";

          console.log(
            `WebSocket closed. Code: ${event.code}, Reason: ${
              event.reason || "-"
            }, Closed by: ${who}`,
          );
        },
      });

    // Wiser BE proxy'si (#12388): once tek kullanimlik ticket alinir, sonra soket
    // BE'ye acilir; ICS soketini BE acar. Dogrudan ICS adresi
    // (bugun demo/prod) ticket'siz, eskisi gibi.
    if (usesBackendProxy(webSocketUrl)) {
      getIcsStreamTicket(interviewId)
        .then((ticket) => {
          if (!cancelled) connect(ticket);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("Stream ticket alinamadi:", err);
          setError("Sunucuya bağlanılamadı. Kayıt başlatılamıyor.");
          chunkProcessorRef.current.setSocketStatus(false);
        });
    } else {
      connect();
    }

    // Setup chunk processor callbacks
    chunkProcessorRef.current.setOnAllChunksSent(() => {
      uploadTimer.stop();
      setTimeout(() => {
        // Reset all states before closing
        resetRecorderStates();
        onClose?.();
        showToast("Video başarıyla kaydedilmiştir.", "success");
      }, 2000);
    });

    chunkProcessorRef.current.setOnBackupSaved((blob: Blob) => {
      // Download backup to user's device
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date();
      const fileName = `backup-${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}_${date
        .getHours()
        .toString()
        .padStart(2, "0")}-${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}-${date
        .getSeconds()
        .toString()
        .padStart(2, "0")}.webm`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Compact view'deyse ana görünüme geç ki kullanıcı hatayı okuyabilsin
      setIsCompact(false);
      uploadTimer.stop();
      uploadTimer.reset();
      setUploadProgress("");
      setError(
        "Videonun gönderilmesi sırasında bir sorun oluştu. Video indirildi.",
      );
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      webSocketManager.disconnect();
      chunkProcessorRef.current.reset();
      timer.reset();
      uploadTimer.reset();
    };
  }, [interviewId, webSocketUrl]);

  const getVideoSources = async () => {
    try {
      // For web, we'll use getDisplayMedia which shows browser's picker
      // We'll just show a placeholder option
      setAvailableSources([
        { id: "screen", name: "Ekran Seç (Tarayıcı seçici açılacak)" },
      ]);
      setSelectedSource("screen");
    } catch (error) {
      console.error("Error getting video sources:", error);
      setError("Ekran kaynakları alınamadı");
    }
  };

  useEffect(() => {
    getVideoSources();
  }, []);

  // Refs for cleanup and beforeunload (avoid stale closure)
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
  useEffect(() => {
    displayStreamRef.current = displayStream;
  }, [displayStream]);

  // Tarayıcı sekmesi kapatılırsa / yenilenirse uyarı
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRecordingRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Geri tuşu / route değişimi: Prompt bazen tetiklenmediği için history.block ile de engelle
  const leaveMessage =
    "Kayıt devam ediyor. Sayfadan ayrılırsanız kayıt sonlanacak ve paylaşım izni kapatılacak. Yine de çıkmak istiyor musunuz?";
  useEffect(() => {
    if (!isRecording) return;
    const unblock = history.block((location, action) => {
      return leaveMessage;
    });
    return () => unblock();
  }, [isRecording, history]);

  // Sayfa değişince (örn. route) kaydı ve izni temizle
  useEffect(() => {
    return () => {
      if (isRecordingRef.current) {
        stopRecording();
        displayStreamRef.current?.getTracks().forEach((t) => t.stop());
      }
    };
  }, [stopRecording]);

  // Request screen selection on mount
  useEffect(() => {
    const getScreenStream = async () => {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true, // Kullanıcıya bu seçeneği sunmak için şart
          //@ts-ignore
          systemAudio: "include",
        });

        // 1. Kontrol: Gelen stream içerisinde ses izi (audio track) var mı?
        const audioTracks = stream.getAudioTracks();

        if (audioTracks.length === 0) {
          // 2. Eğer ses yoksa, mevcut video paylaşımını da durdur (kapat)
          stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());

          // 3. Kullanıcıya neden kapattığımızı açıkla
          alert(
            "Sistem sesini paylaşmadınız! Lütfen paylaşım penceresindeki 'Sistem sesini paylaş' kutucuğunu işaretleyerek tekrar deneyin.",
          );

          // 4. Tekrar ekran seçim ekranını aç
          getScreenStream();
          return;
        }
        setDisplayStream(stream);

        // Show preview
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          await videoRef.current.play();
        }
      } catch (err) {
        console.error("Error getting display media:", err);
        setError("Ekran paylaşım izni reddedildi veya iptal edildi.");
      }
    };

    getScreenStream();

    return () => {
      // Cleanup stream on unmount
      if (displayStream) {
        displayStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Restore video stream when switching from compact to normal view
  useEffect(() => {
    if (!isCompact && videoRef.current && isRecording) {
      const currentStream = videoRef.current.srcObject as MediaStream;
      if (!currentStream || currentStream.getTracks().length === 0) {
        // Re-set the stream if it's lost during view transition
        if (displayStream) {
          videoRef.current.srcObject = displayStream;
          videoRef.current.muted = true;
          videoRef.current
            .play()
            .catch((err) => console.error("Error playing video:", err));
        }
      }
    }
  }, [isCompact, isRecording, displayStream]);

  const handleStartRecording = async () => {
    if (!isConnected) {
      setError("Sunucuyla bağlantı kapalı. Lütfen sayfayı yenileyin.");
      return;
    }

    if (!displayStream) {
      setError("Ekran seçimi yapılmadı. Lütfen sayfayı yenileyin.");
      return;
    }

    setError(null);

    const finalStream = await startRecording();

    if (finalStream) {
      // Set video preview with final stream (includes mixed audio)
      if (videoRef.current) {
        videoRef.current.srcObject = finalStream;
        videoRef.current.muted = true;
        await videoRef.current.play();
      }

      // Timer'ı kayıt başarıyla başladıktan sonra başlat
      timer.start();
      setIsCompact(true);
    } else {
      setError("Kayıt başlatılamadı. Lütfen sayfayı yenileyin.");
    }
  };

  function handleStopRecordingCallback() {
    setIsLoading(true);
    chunkProcessorRef.current.setRecordingStopped(true);

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    timer.stop();
    uploadTimer.start();
    setUploadProgress("Video yükleniyor...");
  }

  const resetRecorderStates = () => {
    // Reset all states to initial values
    setIsCompact(false);
    setIsLoading(false);
    setError(null);
    setUploadProgress("");
    setDisplayStream(null);
    timer.reset();
    uploadTimer.reset();

    // Clear video ref
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Stop all tracks if any
    if (displayStream) {
      displayStream.getTracks().forEach((track) => track.stop());
    }
  };

  const handleStopRecording = () => {
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    stopRecording();
  };

  const handleCloseClick = () => {
    if (isRecording) {
      setShowLeaveWarning(true);
    } else {
      onClose?.();
    }
  };

  const handleConfirmLeave = () => {
    handleStopRecording();
    if (displayStream) {
      displayStream.getTracks().forEach((t) => t.stop());
    }
    setDisplayStream(null);
    setShowLeaveWarning(false);
    // Çıkışta oturum state'ini sıfırla; panel tekrar açıldığında Başlat butonu aktif olsun
    setIsLoading(false);
    setUploadProgress("");
    setError(null);
    timer.reset();
    uploadTimer.reset();
    chunkProcessorRef.current.setRecordingStopped(false);
    onClose?.();
  };

  const handleCancelLeave = () => {
    setShowLeaveWarning(false);
  };

  console.log(!isConnected, isLoading);

  // if (isCompact) {
  //   return (

  //   );
  // }

  return (
    <>
      <Prompt
        when={isRecording}
        message="Kayıt devam ediyor. Sayfadan ayrılırsanız kayıt sonlanacak ve paylaşım izni kapatılacak. Yine de çıkmak istiyor musunuz?"
      />
      <Dialog
        open={showLeaveWarning}
        onClose={handleCancelLeave}
        aria-labelledby="leave-warning-dialog-title"
        aria-describedby="leave-warning-dialog-description"
      >
        <DialogTitle id="leave-warning-dialog-title">
          Kaydı durdurmak üzeresiniz
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="leave-warning-dialog-description">
            Kayıt devam ediyor. Kaydı durdurmak istediğinize emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave} color="primary">
            İptal
          </Button>
          <Button
            onClick={handleConfirmLeave}
            color="error"
            variant="contained"
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          position: "fixed",
          top: 90,
          right: 20,
          zIndex: 9999,
          width: "25vw",
          minWidth: "320px",
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          visibility: isCompact ? "hidden" : "visible",
        }}
      >
        {/* Header with Close Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: `${colors.aiLinearGradient} !important`,
            padding: "0.5rem 1rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <img
              src={Recording}
              alt="video-icon"
              style={{
                background: "#FFFFFF33",
                borderRadius: "10px",
                padding: "5px",
                width: "28px",
                height: "28px",
              }}
            />
            <Typography sx={{ ...textBase(500, colors.white) }}>
              Kayıt Oturumu
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setIsCompact(true)}
              sx={{
                background: "#FFFFFF33",
                width: "28px",
                height: "28px",
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: "#ffffff5e",
                },
              }}
            >
              <Minimize2 color="white" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleCloseClick}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "#ffffff5e",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Metadata Info */}
        {recordingMetadata && (
          <Box
            sx={{
              p: 2,
              m: 2,
              background: colors.neutral.N10,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              border: `1px solid ${colors.neutral.N20}`,
            }}
          >
            {/* Recruiter */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#5DB0DF",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {(
                  recordingMetadata.recruiter.first_name?.[0] || ""
                ).toUpperCase()}
                {(
                  recordingMetadata.recruiter.last_name?.[0] || ""
                ).toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.25,
                  }}
                >
                  <Typography sx={{ ...textSM(500, "#7A869A") }}>
                    İşe Alımcı
                  </Typography>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#1DD75B",
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    ...textSM(600, "#1A2341"),
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {recordingMetadata.recruiter.first_name}{" "}
                  {recordingMetadata.recruiter.last_name}
                </Typography>
              </Box>
            </Box>

            {/* Candidate */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#00C6E6",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {(recordingMetadata.talent.first_name?.[0] || "").toUpperCase()}
                {(recordingMetadata.talent.last_name?.[0] || "").toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.25,
                  }}
                >
                  <Typography sx={{ ...textSM(500, "#7A869A") }}>
                    Aday
                  </Typography>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#1DD75B",
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    ...textSM(600, "#1A2341"),
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {recordingMetadata.talent.first_name}{" "}
                  {recordingMetadata.talent.last_name}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Error Display */}
        {(error || recorderError) && (
          <Alert severity="error" sx={{ m: 2, borderRadius: "10px" }}>
            {error || recorderError}
          </Alert>
        )}

        {/* Upload Progress */}
        {uploadProgress && (
          <Alert severity="info" sx={{ m: 2, borderRadius: "10px" }}>
            {uploadProgress}
          </Alert>
        )}

        {/* Video Preview */}
        <Box
          sx={{
            borderRadius: 2,
            border: `10px solid ${colors.neutral.N30}`,
            m: 2,
            position: "relative",
          }}
        >
          <video
            ref={videoRef}
            style={{
              height: "auto",
              minHeight: 200,
              maxHeight: 250,
              width: "100%",
              display: "block",
            }}
          />

          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>

        {/* Upload Timer */}
        {uploadTimer.isRunning && (
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ...textSM }}
            >
              Yükleme süresi: {uploadTimer.formattedTime}
            </Typography>
          </Box>
        )}

        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            m: 2,
            padding: "12px 16px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
          }}
        >
          {/* Left side - Recording indicator with timer */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: isRecording ? "#d32f2f" : "#9e9e9e",
                animation: isRecording ? "pulse 2s infinite" : "none",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
            <Typography sx={{ ...textSM(500, "#374151") }}>Kayıt</Typography>
            <Typography
              sx={{ ...textSM(500, "#374151"), fontFamily: "monospace" }}
            >
              {timer.formattedTime}
            </Typography>
          </Box>

          {/* Right side - Control buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* {isRecording && (
              <IconButton
                size="small"
                sx={{
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <img
                  src={Stop}
                  alt={"Durdur"}
                  style={{ width: 26, height: 26 }}
                />
              </IconButton>
            )} */}

            {isRecording ? (
              <Button
                variant="contained"
                onClick={handleStopRecording}
                disabled={isLoading}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                sx={{
                  fontSize: "12px",
                  padding: "8px 12px",
                  borderRadius: "14px",
                  textTransform: "none",
                  backgroundColor: "#dc2626",
                  color: "#fff",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#b91c1c",
                  },
                  "&:disabled": {
                    backgroundColor: "#fca5a5",
                    color: "#fff",
                  },
                }}
              >
                Durdur
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleStartRecording}
                disabled={!isConnected || isLoading}
                sx={{
                  fontSize: "12px",
                  padding: "8px 12px",
                  borderRadius: "14px",
                  textTransform: "none",
                  backgroundColor: "#2563eb",
                  color: "#fff",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#1d4ed8",
                  },
                }}
              >
                {isLoading ? "Yükleniyor..." : "Başlat"}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          position: "fixed",
          top: 90,
          right: 20,
          zIndex: 9999,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minWidth: "300px",
          visibility: isCompact ? "visible" : "hidden",
        }}
      >
        {/* Left side - Recording indicator with timer */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: isRecording ? "#d32f2f" : "#9e9e9e",
              animation: isRecording ? "pulse 2s infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          />
          <Typography
            sx={{
              ...textSM(500, "#374151"),
            }}
          >
            Kayıt
          </Typography>
          <Typography
            sx={{
              ...textSM(500, "#374151"),
            }}
          >
            {timer.formattedTime}
          </Typography>
        </Box>

        {/* Right side - Control buttons */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <IconButton
            size="small"
            onClick={() => setIsCompact(false)}
            sx={{
              background: "#FFFFFF33",
              width: "28px",
              height: "28px",
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "#ffffff5e",
              },
            }}
          >
            <Maximize2 />
          </IconButton>

          {isRecording ? (
            <Button
              variant="contained"
              onClick={handleStopRecording}
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "14px",
                textTransform: "none",
                backgroundColor: "#dc2626",
                color: "#fff",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#b91c1c",
                },
                "&:disabled": {
                  backgroundColor: "#fca5a5",
                  color: "#fff",
                },
              }}
            >
              Durdur
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleStartRecording}
              disabled={!isConnected || isLoading}
              sx={{
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "14px",
                textTransform: "none",
                backgroundColor: "#2563eb",
                color: "#fff",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#1d4ed8",
                },
              }}
            >
              {isLoading ? "Yükleniyor..." : "Kaydı Başlat"}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ScreenRecorder;
